import { getOrCreateI18n, I18n } from '@affine/i18n';
import { ArrowRightIcon, EnterIcon } from '@blocksuite/affine/components/icons';
import { WithDisposable } from '@blocksuite/affine/global/lit';
import { ColorScheme } from '@blocksuite/affine/model';
import {
  EditorHost,
  PropTypes,
  requiredProperties,
} from '@blocksuite/affine/std';
import { css, html, LitElement, nothing } from 'lit';
import { property, query } from 'lit/decorators.js';

import { menuItemStyles } from './styles';
import type { AIItemConfig } from './types';

@requiredProperties({
  host: PropTypes.instanceOf(EditorHost),
  item: PropTypes.object,
})
export class AIItem extends WithDisposable(LitElement) {
  override connectedCallback() {
    super.connectedCallback();

    const i18n = getOrCreateI18n();
    const onLanguageChanged = () => this.requestUpdate();
    i18n.on('languageChanged', onLanguageChanged);
    this.disposables.add(() => i18n.off('languageChanged', onLanguageChanged));
  }

  static override styles = css`
    ${menuItemStyles}
  `;

  override render() {
    const { item } = this;
    const className = item.testId;
    const itemName = I18n.t(item.label ?? item.name);
    const testId = item.testId;

    return html`<div
      data-testid=${testId}
      data-app-theme=${this.theme}
      class="menu-item ${className}"
      @pointerdown=${(e: MouseEvent) => e.stopPropagation()}
      @click=${() => {
        this.onClick?.();
        if (typeof item.handler === 'function') {
          item.handler(this.host);
        }
      }}
    >
      <span class="item-icon">${item.icon}</span>
      <div class="item-name">
        ${itemName}${
          item.beta
            ? html`<div class="item-beta">
                (${I18n.t({ i18nKey: 'com.affine.ai.edgeless-menu.beta' })})
              </div>`
            : nothing
        }
      </div>
      ${
        item.subItem
          ? html`<span class="arrow-right-icon">${ArrowRightIcon}</span>`
          : html`<span class="enter-icon">${EnterIcon}</span>`
      }
    </div>`;
  }

  @property({ attribute: false })
  accessor host!: EditorHost;

  @property({ attribute: false })
  accessor item!: AIItemConfig;

  @query('.menu-item')
  accessor menuItem: HTMLDivElement | null = null;

  @property({ attribute: false })
  accessor onClick: (() => void) | undefined;

  @property({ attribute: false })
  accessor theme: ColorScheme = ColorScheme.Light;
}

declare global {
  interface HTMLElementTagNameMap {
    'ai-item': AIItem;
  }
}
